/**
 * APP-AI-2: the "AI suggestions" group under the unmatched list.
 *
 * Suggestions are shown, never applied: a unit match needs Accept (or a tick
 * plus "Accept selected"); a date range needs "Use this". The evidence is the
 * server's redacted fragment — words that never left the app read as "·".
 */

import { Loader2, X } from 'lucide-react';
import { formatDayRange } from '@/lib/unitCodeParser';
import type { DateRangeSuggestion, PasteSuggestion, UnitSuggestion } from '@/lib/pasteAssistApi';
import type { usePasteAssist } from './usePasteAssist';

interface AiSuggestionsProps {
  assist: ReturnType<typeof usePasteAssist>;
  /** There is something the rules could not resolve (unmatched codes or no dates). */
  canRequest: boolean;
  onRequest: () => void;
  onAcceptUnits: (suggestions: UnitSuggestion[]) => void;
  onUseDates: (suggestion: DateRangeSuggestion) => void;
}

const PROVIDER_LABEL: Record<string, string> = { anthropic: 'Claude', openai: 'ChatGPT' };

function Evidence({ s }: { s: PasteSuggestion }) {
  const [start, end] = s.span;
  return (
    <span className="min-w-0 truncate font-mono text-[11px] text-muted-foreground" title={s.fragment_text}>
      {s.fragment_text.slice(0, start)}
      <mark className="rounded-sm bg-accent px-0.5 text-foreground">{s.fragment_text.slice(start, end)}</mark>
      {s.fragment_text.slice(end)}
    </span>
  );
}

const pct = (c: number) => `${Math.round(c * 100)}%`;

function unavailableText(reason?: string) {
  if (reason === 'state_store_unavailable') return 'AI suggestions are unavailable right now.';
  if (reason === 'all_failed' || reason === 'budget_exhausted') return 'The AI providers did not answer. Try again, or fix the codes by hand.';
  return 'AI suggestions are turned off.';
}

export function AiSuggestions({ assist, canRequest, onRequest, onAcceptUnits, onUseDates }: AiSuggestionsProps) {
  if (!assist.available) return null;
  const { response, loading, error, visible, selected } = assist;
  if (!response && !loading && !error && !canRequest) return null;

  const units = visible.filter((s): s is UnitSuggestion => s.kind === 'unit');
  const dates = visible.filter((s): s is DateRangeSuggestion => s.kind === 'date_range');
  const ticked = units.filter((s) => selected.includes(s.suggestion_id));

  const accept = (items: UnitSuggestion[]) => {
    onAcceptUnits(items);
    assist.markAccepted(items.map((s) => s.suggestion_id));
  };

  let status: string | null = null;
  if (error) status = error;
  else if (response && !response.available) status = unavailableText(response.reason);
  else if (response?.skipped === 'nothing_to_send') status = 'Nothing left for AI to look at.';
  else if (response && visible.length === 0) status = (response.suggestions?.length ?? 0) > 0 ? 'All suggestions handled.' : 'No suggestions.';

  return (
    <section aria-label="AI suggestions" className="flex flex-col gap-1.5 border-t border-border pt-3">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">AI suggestions</span>
        {response?.served_by && (
          <span className="text-[11px] text-muted-foreground">
            · {PROVIDER_LABEL[response.served_by] ?? response.served_by}{response.failed_over ? ' (backup)' : ''}
          </span>
        )}
        <span className="flex-1" />
        {ticked.length > 0 && (
          <button
            type="button"
            onClick={() => accept(ticked)}
            className="h-[26px] rounded-md bg-[#8FB560] px-2.5 text-xs font-medium text-black transition-[filter] hover:brightness-95"
          >
            Accept selected ({ticked.length})
          </button>
        )}
        {(!response || !response.available) && (
          <button
            type="button"
            onClick={onRequest}
            disabled={loading || !canRequest}
            className="flex h-[26px] items-center gap-1.5 rounded-md border border-border px-2.5 text-xs text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:text-muted-foreground"
          >
            {loading && <Loader2 className="h-3 w-3 animate-spin" />}
            {loading ? 'Asking…' : 'Suggest matches'}
          </button>
        )}
      </div>

      {!response && !loading && (
        <span className="text-[11px] text-muted-foreground">
          Sends only the unmatched codes and dates — names, emails, phones and addresses are removed first.
        </span>
      )}

      {status && <span className="text-xs text-muted-foreground">{status}</span>}

      {units.map((s) => (
        <div key={s.suggestion_id} className="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-[7px]">
          <input
            type="checkbox"
            aria-label={`Select ${s.unit_code}`}
            checked={selected.includes(s.suggestion_id)}
            onChange={() => assist.toggleSelected(s.suggestion_id)}
            className="h-3.5 w-3.5 flex-none accent-[#8FB560]"
          />
          <div className="flex min-w-0 flex-1 flex-col">
            <Evidence s={s} />
            <span className="text-[13px] text-foreground">
              → <span className="font-mono">{s.unit_code}</span>{' '}
              <span className="text-[11px] tabular-nums text-muted-foreground">{pct(s.confidence)}</span>
            </span>
          </div>
          <button
            type="button"
            onClick={() => accept([s])}
            className="h-[26px] flex-none rounded-md border border-border px-2.5 text-xs text-foreground transition-colors hover:bg-accent"
          >
            Accept
          </button>
          <button
            type="button"
            onClick={() => assist.dismiss(s.suggestion_id)}
            aria-label={`Dismiss ${s.unit_code}`}
            className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-[13px] w-[13px]" />
          </button>
        </div>
      ))}

      {dates.map((s) => (
        <div key={s.suggestion_id} className="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-[7px]">
          <div className="flex min-w-0 flex-1 flex-col">
            <Evidence s={s} />
            <span className="text-[13px] text-foreground">
              Dates {formatDayRange(s.start, s.end)}{' '}
              <span className="text-[11px] tabular-nums text-muted-foreground">{pct(s.confidence)}</span>
            </span>
          </div>
          <button
            type="button"
            onClick={() => { onUseDates(s); assist.markAccepted([s.suggestion_id], 'date_range'); }}
            className="h-[26px] flex-none rounded-md border border-border px-2.5 text-xs text-foreground transition-colors hover:bg-accent"
          >
            Use this
          </button>
          <button
            type="button"
            onClick={() => assist.dismiss(s.suggestion_id)}
            aria-label="Dismiss dates"
            className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-[13px] w-[13px]" />
          </button>
        </div>
      ))}
    </section>
  );
}
